<template>
  <el-dialog
    title="选择后续节点"
    :visible="show"
    width="700px"
    append-to-body
    @close="handleClose"
  >
    <div class="dialog-body">
      <!-- Left panel: chapter list -->
      <div class="chapter-panel">
        <div class="panel-header">章节</div>
        <div
          class="chapter-item"
          :class="{ active: selectedChapterId === null }"
          @click="selectChapter(null)"
        >
          全部
        </div>
        <div
          v-for="chapter in chapters"
          :key="chapter.id"
          class="chapter-item"
          :class="{ active: selectedChapterId === chapter.id }"
          @click="selectChapter(chapter.id)"
        >
          {{ chapter.title }}
        </div>
      </div>

      <!-- Right panel: node list -->
      <div class="node-panel">
        <div class="panel-header">节点</div>
        <div class="node-list">
          <!-- "none" option -->
          <el-radio-group v-model="selectedNodeId">
            <div class="node-row">
              <el-radio :label="null">
                <span class="node-label">（不跳转）</span>
              </el-radio>
            </div>
            <template v-if="selectedChapterId !== null">
              <div
                v-for="item in filteredFlatItems"
                :key="item.node.id"
                class="node-row"
                :class="{ 'is-current': item.node.id === currentNodeId }"
              >
                <el-radio :label="item.node.id" :disabled="item.node.id === currentNodeId">
                  <span
                    class="node-label"
                    :style="{ paddingLeft: item.depth * 16 + 'px' }"
                  >
                    <el-tag size="mini" :type="item.node.node_type === 'ai' ? 'warning' : 'info'" style="margin-right: 4px;">
                      {{ item.node.node_type === 'ai' ? 'AI' : '固定' }}
                    </el-tag>
                    #{{ item.node.id }} {{ item.node.title }}
                  </span>
                </el-radio>
              </div>
              <div v-if="filteredFlatItems.length === 0" class="empty-tip">该章节暂无节点</div>
            </template>
            <template v-else>
              <template v-for="chapter in chapters">
                <div :key="'ch-' + chapter.id" class="chapter-section-header">{{ chapter.title }}</div>
                <div
                  v-for="item in getFlatItemsByChapter(chapter.id)"
                  :key="item.node.id"
                  class="node-row"
                  :class="{ 'is-current': item.node.id === currentNodeId }"
                >
                  <el-radio :label="item.node.id" :disabled="item.node.id === currentNodeId">
                    <span
                      class="node-label"
                      :style="{ paddingLeft: item.depth * 16 + 'px' }"
                    >
                      <el-tag size="mini" :type="item.node.node_type === 'ai' ? 'warning' : 'info'" style="margin-right: 4px;">
                        {{ item.node.node_type === 'ai' ? 'AI' : '固定' }}
                      </el-tag>
                      #{{ item.node.id }} {{ item.node.title }}
                    </span>
                  </el-radio>
                </div>
                <div v-if="getFlatItemsByChapter(chapter.id).length === 0" :key="'empty-' + chapter.id" class="empty-tip">暂无节点</div>
              </template>
              <div v-if="noChapterNodes.length > 0" :key="'ch-none'" class="chapter-section-header">（未分配章节）</div>
              <div
                v-for="item in noChapterNodes"
                :key="item.node.id"
                class="node-row"
                :class="{ 'is-current': item.node.id === currentNodeId }"
              >
                <el-radio :label="item.node.id" :disabled="item.node.id === currentNodeId">
                  <span class="node-label">
                    <el-tag size="mini" :type="item.node.node_type === 'ai' ? 'warning' : 'info'" style="margin-right: 4px;">
                      {{ item.node.node_type === 'ai' ? 'AI' : '固定' }}
                    </el-tag>
                    #{{ item.node.id }} {{ item.node.title }}
                  </span>
                </el-radio>
              </div>
            </template>
          </el-radio-group>
        </div>
      </div>
    </div>

    <span slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </span>
  </el-dialog>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'
import { GameNode, getNodes } from '@/api/node'
import { Chapter } from '@/api/chapter'
import { buildNodeTree, flattenTree, FlatTreeItem } from '@/utils/nodeTree'

@Component
export default class NextNodeSelectDialog extends Vue {
  @Prop({ default: false }) readonly show!: boolean
  @Prop({ default: () => [] }) readonly allNodes!: GameNode[]
  @Prop({ default: () => [] }) readonly chapters!: Chapter[]
  @Prop({ default: null }) readonly currentNodeId!: number | null
  @Prop({ default: 0 }) readonly gameId!: number

  selectedChapterId: number | null = -1 // -1 means "not yet initialized"
  selectedNodeId: number | null = null

  // Cache of flat tree items per chapter (use plain object for Vue 2 reactivity)
  flatItemsCache: Record<number, FlatTreeItem[]> = {}
  allFlatItems: FlatTreeItem[] = []

  @Watch('show')
  onShowChange(val: boolean) {
    if (val) {
      this.initSelection()
    }
  }

  @Watch('allNodes')
  onAllNodesChange() {
    this.rebuildCache()
  }

  initSelection() {
    this.rebuildCache()

    // Default selectedNodeId to null (no jump)
    this.selectedNodeId = null

    // Determine which chapter to default to
    if (this.currentNodeId !== null) {
      const currentNode = this.allNodes.find(n => n.id === this.currentNodeId)
      if (currentNode && currentNode.game_chapter_id != null) {
        this.selectedChapterId = currentNode.game_chapter_id
        return
      }
    }

    // Fall back to first chapter
    if (this.chapters.length > 0) {
      this.selectedChapterId = this.chapters[0].id
    } else {
      this.selectedChapterId = null
    }
  }

  rebuildCache() {
    const cache: Record<number, FlatTreeItem[]> = {}

    // Build flat items only for chapters that have nodes in allNodes
    for (const chapter of this.chapters) {
      const chapterNodes = this.allNodes.filter(n => n.game_chapter_id === chapter.id)
      if (chapterNodes.length > 0) {
        const tree = buildNodeTree(chapterNodes)
        cache[chapter.id] = flattenTree(tree)
      }
    }

    this.flatItemsCache = cache
    // All flat items (for "all chapters" view)
    this.allFlatItems = this.allNodes.map(n => ({ node: n, depth: 0, branchParentId: null, branchEndingIndex: -1 }))
  }

  get filteredFlatItems(): FlatTreeItem[] {
    if (this.selectedChapterId === null) {
      return this.allFlatItems
    }
    return this.flatItemsCache[this.selectedChapterId] || []
  }

  get noChapterNodes(): FlatTreeItem[] {
    const chapterIds = new Set(this.chapters.map(c => c.id))
    return this.allNodes
      .filter(n => !n.game_chapter_id || !chapterIds.has(n.game_chapter_id))
      .map(n => ({ node: n, depth: 0, branchParentId: null, branchEndingIndex: -1 }))
  }

  getFlatItemsByChapter(chapterId: number): FlatTreeItem[] {
    return this.flatItemsCache[chapterId] || []
  }

  async selectChapter(chapterId: number | null) {
    this.selectedChapterId = chapterId
    // 如果缓存里没有该章节的节点，按需加载
    if (chapterId !== null && !(chapterId in this.flatItemsCache)) {
      try {
        const res = await getNodes(this.gameId, chapterId)
        const nodes = res.data || []
        const tree = buildNodeTree(nodes)
        const flat = flattenTree(tree)
        this.$set(this.flatItemsCache, chapterId, flat)
      } catch (e) {
        // ignore
      }
    }
  }

  handleClose() {
    this.$emit('update:show', false)
  }

  handleConfirm() {
    this.$emit('select', this.selectedNodeId)
    this.handleClose()
  }
}
</script>

<style scoped lang="scss">
.dialog-body {
  display: flex;
  min-height: 400px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.chapter-panel {
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid #e4e7ed;
  overflow-y: auto;
  background: #fafafa;
}

.node-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  border-bottom: 1px solid #e4e7ed;
  background: #f5f7fa;
}

.chapter-item {
  padding: 10px 14px;
  font-size: 13px;
  color: #303133;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;

  &:hover {
    background: #ecf5ff;
    color: #409eff;
  }

  &.active {
    background: #ecf5ff;
    color: #409eff;
    font-weight: 600;
  }
}

.node-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.node-row {
  padding: 6px 14px;
  transition: background 0.1s;

  &:hover {
    background: #f5f7fa;
  }

  &.is-current {
    background: #f0f9eb;
  }

  .el-radio {
    width: 100%;
    display: flex;
    align-items: center;
    white-space: normal;
    word-break: break-all;
  }
}

.node-label {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
}

.chapter-section-header {
  padding: 6px 14px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #909399;
  background: #f5f7fa;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  margin-top: 4px;
}

.empty-tip {
  padding: 10px 14px;
  font-size: 12px;
  color: #c0c4cc;
}
</style>
